import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {MatTable, MatTableModule} from "@angular/material/table";
import {AppointmentsService} from "../../services/appointments.service";
import {MatTooltipModule} from "@angular/material/tooltip";
import {MatIconModule} from "@angular/material/icon";
import {MatButtonModule} from "@angular/material/button";
import {OrgDatePipe, PageLoaderService, SnackbarService} from '@allaihealth/ng-platform';
import {Router, RouterLink} from '@angular/router';
import {AppointmentRow, AppointmentTable} from "../../lib/appointment-table";
import {AgeSexPipe} from "../../lib/age-sex.pipe";
import {AppointmentStatusPillComponent} from "../../components/pills/appointment-status-pill";
import {AppointmentTasksPillComponent} from "../../components/pills/appointment-tasks-pill";
import {MatMenuModule} from "@angular/material/menu";
import {AppointmentStatus} from "intake-shared";
import {MatSelectModule} from "@angular/material/select";
import {FormControl, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {MatDatepicker, MatDatepickerInput, MatDatepickerToggle} from "@angular/material/datepicker";
import {MatInput} from "@angular/material/input";
import {provideNativeDateAdapter} from "@angular/material/core";
import {MatDialog} from "@angular/material/dialog";
import {firstValueFrom} from "rxjs";
import {ResendIntakeDialog} from "../../components/dialog/resend-intake-dialog";
import {NotificationService} from "../../services/notification.service";
import {SurveyQuestions} from "@allaihealth/survey";
import {User} from "@allaihealth/util";
import {MatCheckbox, MatCheckboxChange} from "@angular/material/checkbox";

/** The appointments list page */
@Component({
	selector: 'app-appointments',
	imports: [
		MatTableModule,
		MatIconModule,
		MatButtonModule,
		MatTooltipModule,
		MatMenuModule,
		AppointmentStatusPillComponent,
		AppointmentTasksPillComponent,
		OrgDatePipe,
		AgeSexPipe,
		RouterLink,
		MatSelectModule,
		FormsModule,
		MatDatepicker,
		MatDatepickerInput,
		MatDatepickerToggle,
		MatInput,
		ReactiveFormsModule,
		MatCheckbox
	],
	templateUrl: './appointments.component.html',
	styleUrl: './appointments.component.scss',
	providers: [provideNativeDateAdapter()]
})
export class AppointmentsComponent implements OnInit, OnDestroy {
	protected readonly AppointmentStatus = AppointmentStatus;
	dateControl:FormControl<Date>;
	searchControl:FormControl<string>;

	/** Columns in the table */
	readonly displayedColumns:(keyof AppointmentRow | string)[] = ['appointmentTime', 'patientName', 'appointmentType', 'providerName', 'status', 'tasks', 'actions'];

	@ViewChild(MatTable, {static: true})
	private table:MatTable<AppointmentRow>;

	protected appointmentTable:AppointmentTable;

	private _detailSubscription:any;

	protected _providerChecks:any = {};
	protected _statusChecks:any = {};
	protected _taskChecks:any = {};

	protected allProvidersChecked = true;
	protected allStatusesChecked = true;
	protected allTasksChecked = true;

	constructor(protected appointmentsService:AppointmentsService,
	            private router:Router,
	            public dialog:MatDialog,
	            private snackBarService:SnackbarService,
	            private notificationService:NotificationService,
	            private pageLoaderService:PageLoaderService) {

		this._detailSubscription = this.appointmentsService.onDetailUpdate.subscribe((_values:any) => {
			if(this.appointmentTable) {
				this.appointmentTable.refresh();
			}
		})

	}

	ngOnInit() {
		this.dateControl = new FormControl(this.appointmentsService.currentDate, Validators.required);
		this.dateControl.valueChanges.subscribe((value) => this.appointmentsService.currentDate = value);
		this.appointmentTable = new AppointmentTable(this.appointmentsService);
		this.appointmentTable.connect();
		this.searchControl = new FormControl(this.appointmentTable.currentFilter);
		this.checkAllProviders();
		this.checkAllStatuses();
		this.checkAllTasks();
	}

	ngOnDestroy() {
		this.appointmentTable.disconnect();
		this._detailSubscription?.unsubscribe();
		this._detailSubscription = undefined
	}

	/** Open an appointment that was clicked on */
	goto(row:AppointmentRow) {
		this.router.navigate(['/', 'appointments', row.appointmentId]).then();
	}

	get dataSource() {
		return this.appointmentTable.dataSource;
	}

	protected changeStatus(row:any, value:AppointmentStatus) {
		if(!row.tasks) {
			row.tasks = {};
		}
		row.tasks.status = value;
		this.appointmentsService.updateStatus(row.appointmentId, value).then();
	}

	protected getPillClassNameInsurance(row:AppointmentRow, task:any) {
		if(this.appointmentsService.getPatientInfo(row.patient.id)?.data[SurveyQuestions.InsuranceStatus] == 'SELF_PAY') {
			if(row.tasks.status == AppointmentStatus.CheckedOut) {
				return "done-checked-out";
			}
			return "done-not-checked-out";
		}
		return this.getPillClassName(row, task);
	}

	protected getPillClassName(row:AppointmentRow, task:any):string {

		const percent = this.getPercent(task);
		if(!row.tasks.status) {
			return this.getProgressClass(percent, "not-started", "in-progress", "done-not-checked-out");
		}
		switch(row.tasks.status) {
			case AppointmentStatus.Scheduled:
				return this.getProgressClass(percent, "not-started", "in-progress", "done-not-checked-out");
			case AppointmentStatus.CheckedOut:
				return this.getProgressClass(percent, "not-started-warning", "in-progress", "done-checked-out");
			default:
				return this.getProgressClass(percent, "not-started-warning", "in-progress", "done-not-checked-out");
		}
	}

	private getProgressClass(percent:number, notStarted:string, started:string, done:string) {
		if(percent == 0) {
			return notStarted;
		}
		if(percent >= 100) {
			return done;
		}
		return started;
	}

	private getPercent(task:any) {
		const values = Object.values(task);
		const trues = values.filter(value => value === true);
		return trues.length / values.length * 100;
	}

	protected getResendToolTip(row:any) {
		if(!this.hasContactInfo(row)) {
			return "Not subscribed to email or SMS.";
		}
		return "Resend intake";
	}


	protected getPhoneToolTip(row:any) {
		if(!row.patient.phone) {
			return "No phone number on file";
		}
		return `Mobile ${row.patient.phone}`;
	}

	setToday() {
		this.dateControl.setValue(new Date());
	}

	/** Is the date in the date control today? */
	get isToday() {
		const today = new Date().setHours(0, 0, 0, 0);
		return this.dateControl.value.setHours(0, 0, 0, 0) == today;
	}

	protected search($event?:any) {
		if($event) {
			this.appointmentTable.currentFilter = $event.target.value;
		} else {
			this.searchControl.setValue('');
			this.appointmentTable.currentFilter = '';
		}
	}

	protected async resendIntake(row:AppointmentRow) {
		const ref = this.dialog.open(ResendIntakeDialog, {
			data: {title: `Resend intake to ${row.patientName}?`},
			width: 'auto'
		});
		const response = await firstValueFrom(ref.afterClosed());
		if(response) {

			this.pageLoaderService.show("Sending intake to patient...");
			const sendIntakeResponse = await this.notificationService.resendIntake(row);
			this.pageLoaderService.close();

			if(sendIntakeResponse) {
				this.snackBarService.message(`Intake resent to ${row.patientName}.`);
			} else {
				this.snackBarService.error(`Error resending intake to ${row.patientName}.`);
			}

		}
	}

	protected hasContactInfo(row:AppointmentRow):boolean {
		return this.notificationService.validateEmail(row.patient) || this.notificationService.validateSms(row.patient);
	}

	protected getDisplayName(row:AppointmentRow) {
		const info = this.appointmentsService.getPatientInfo(row.patient.id);
		if(info?.data[SurveyQuestions.PreferredName]?.trim().length > 0) {
			return `${info?.data[SurveyQuestions.PreferredName]} ${row.patient.last_name}`;
		}
		return row.patientName;
	}

	protected getCheckClass(id:string, checks:any) {
		return checks[id] ? "" : "hidden-check";
	}

	protected toggleValue(id:string, values:any) {
		values[id] = !values[id];
		return new Set(Object.keys(values).filter((key:string) => values[key]));
	}

	protected getFilterClass(currentChecks:any, allChecks?:any[]) {
		const checkCount = Object.values(currentChecks).filter(check => check).length;
		if(checkCount < allChecks.length) {
			return "active-filter";
		}
		return "";
	}

	protected setTaskChecks(tasks:Set<string>) {
		this.allTasksChecked = tasks.size == this.appointmentsService.sortedTasks.length;
		this.appointmentTable.selectedTasks = tasks;
	}

	protected clearAllTasks() {
		this._taskChecks = {};
		this.appointmentTable.selectedTasks = new Set(this.appointmentsService.sortedTasks);
	}

	private checkAllTasks() {
		this.appointmentsService.sortedTasks.forEach((task:string) => {
			this._taskChecks[task] = true;
		});
		this.appointmentTable.selectedTasks = new Set(this.appointmentsService.sortedTasks);
	}

	protected checkTasks($event:MatCheckboxChange) {
		if($event.checked) {
			this.checkAllTasks();
		} else {
			this.clearAllTasks();
		}
	}


	protected formatStatus(status:string) {
		return status.replace(/_/g, "_").toUpperCase();
	}

	protected setStatusChecks(statuses:Set<string>) {
		this.allStatusesChecked = statuses.size == this.appointmentsService.sortedStatuses.length;
		this.appointmentTable.selectedStatuses = statuses;
	}

	protected clearAllStatuses() {
		this._statusChecks = {};
		this.appointmentTable.selectedStatuses = new Set();
	}

	private checkAllStatuses() {
		this.appointmentsService.sortedStatuses.forEach((status:string) => {
			this._statusChecks[this.formatStatus(status)] = true;
		});
		this.appointmentTable.selectedStatuses = this.appointmentTable.selectedStatuses = new Set(this.appointmentsService.sortedStatuses.map((status:string) => this.formatStatus(status)));
	}

	protected checkStatuses($event:MatCheckboxChange) {
		if($event.checked) {
			this.checkAllStatuses();
		} else {
			this.clearAllStatuses();
		}
	}

	protected setProviderChecks(providerIds:Set<string>) {
		this.allProvidersChecked = providerIds.size == this.appointmentsService.sortedProviders.length;
		this.appointmentTable.selectedProviderIds = providerIds;
	}

	protected checkProviders($event:MatCheckboxChange) {
		if($event.checked) {
			this.checkAllProviders();
		} else {
			this.clearAllProviders();
		}
	}

	private checkAllProviders() {
		this.appointmentsService.sortedProviders.forEach((provider:User) => {
			this._providerChecks[provider.id] = true;
		});
		this.appointmentTable.selectedProviderIds = new Set(this.appointmentsService.sortedProviders.map((provider:User) => provider.id));
	}

	protected clearAllProviders() {
		this._providerChecks = {};
		this.appointmentTable.selectedProviderIds = new Set();
	}

}


